import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentIcon from "@mui/icons-material/Payment";

export default function StaffSalaryPayments() {
  const navigate = useNavigate();
  const { staffId } = useParams();
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState(null);
  const [salaryRecords, setSalaryRecords] = useState([]);

  useEffect(() => {
    fetchData();
  }, [staffId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch staff info
      const staffResponse = await fetch(`${API_BASE_URL}/api/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (staffResponse.ok) {
        const staffData = await staffResponse.json();
        setStaffInfo(staffData);
        
        // Generate all months from joining date to current
        const months = generateAllMonths(staffData);
        setSalaryRecords(months);
      } else {
        toast.error("Failed to load staff details");
        navigate("/staff");
        return;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Generate all months from joining date to current month (same logic as AdminStaffAttendanceSalary)
  const generateAllMonths = (staff) => {
    const months = [];
    const currentDate = new Date();
    
    if (!staff || !staff.JoiningDate) {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const monthYear = currentDate.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      
      return [{
        monthYear,
        month,
        year,
        SalaryMonth: `${year}-${String(month).padStart(2, '0')}`,
      }];
    }
    
    const startDate = new Date(staff.JoiningDate);
    let iterDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    
    while (iterDate <= endDate) {
      const month = iterDate.getMonth() + 1;
      const year = iterDate.getFullYear();
      const monthYear = iterDate.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      
      months.push({
        monthYear,
        month,
        year,
        SalaryMonth: monthYear,
      });
      
      iterDate.setMonth(iterDate.getMonth() + 1);
    }
    
    return months.reverse();
  };

  const handleViewPayments = (month, year) => {
    navigate(`/admin/staff/${staffId}/salary-details/${month}/${year}`, {
      state: staffInfo
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton
            onClick={() => navigate("/staff")}
            sx={{
              mr: 2,
              bgcolor: "#8B7355",
              color: "white",
              "&:hover": { bgcolor: "#6D5A43" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: "#8B7355",
                textDecoration: "underline",
                textDecorationThickness: 2,
                textUnderlineOffset: 8,
              }}
            >
              SALARY PAYMENT MANAGEMENT
            </Typography>
            {staffInfo && (
              <Typography variant="h6" sx={{ color: "#6D5A43", mt: 1 }}>
                {staffInfo.FullName} - {staffInfo.Designation}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Monthly Salary Records */}
        <Card sx={{ boxShadow: 3 }}>
          <CardContent>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 2, color: "#8B7355" }}
            >
              Monthly Salary Records
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box>
              {salaryRecords.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No months to display
                </Typography>
              ) : (
                salaryRecords.map((record) => (
                  <Card
                    key={`${record.year}-${record.month}`}
                    sx={{
                      mb: 2,
                      bgcolor: "#F9F9F9",
                      boxShadow: 1,
                      "&:hover": { boxShadow: 3 },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#8B7355" }}>
                            {record.monthYear}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Click to view and manage salary payments
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          startIcon={<PaymentIcon />}
                          onClick={() => handleViewPayments(record.month, record.year)}
                          sx={{
                            bgcolor: "#0288D1",
                            color: "white",
                            "&:hover": { bgcolor: "#0277BD" },
                          }}
                        >
                          Salary Payment
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
